import React, { useState, useEffect } from 'react';
import { X, DollarSign, CreditCard, Calendar, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import api from '../services/api';
import Modal from './Common/Modal';

const PAYMENT_TYPES = [
    { value: 'rent', label: 'Rent', icon: DollarSign },
    { value: 'deposit', label: 'Deposit', icon: CreditCard },
    { value: 'late_fee', label: 'Late Fee', icon: AlertCircle },
    { value: 'utility', label: 'Utility', icon: CreditCard },
    { value: 'maintenance', label: 'Maint', icon: CreditCard },
    { value: 'other', label: 'Other', icon: CreditCard },
];

// Kenyan school term definitions
const TERM_OPTIONS = [
    { term: 1, label: 'Term 1 (Jan–Mar)', startMonth: 0, startDay: 1, endMonth: 2, endDay: 31 },
    { term: 2, label: 'Term 2 (May–Jul)', startMonth: 4, startDay: 1, endMonth: 6, endDay: 31 },
    { term: 3, label: 'Term 3 (Sep–Nov)', startMonth: 8, startDay: 1, endMonth: 10, endDay: 30 },
];

/**
 * Return start/end dates for a Kenyan school term in a given year.
 */
function getTermDates(termNumber, year) {
    const def = TERM_OPTIONS.find(t => t.term === termNumber);
    if (!def) return null;
    return {
        start: new Date(year, def.startMonth, def.startDay),
        end: new Date(year, def.endMonth, def.endDay),
    };
}

/**
 * Generate ISO week range string like "Week of May 25" for a given date.
 */
function getWeekRange(date) {
    const d = new Date(date);
    const dayOfWeek = d.getDay();
    const monday = new Date(d);
    monday.setDate(d.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: monday, end: sunday };
}

// ─── Month options generator (reusable) ─────────────────────────────────────
function getMonthOptions() {
    return [...Array(12)].map((_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        return {
            value,
            label: date.toLocaleString('default', { month: 'long', year: 'numeric' }),
            year: date.getFullYear(),
            month: date.getMonth() + 1,
        };
    });
}

// ─── Week options generator ─────────────────────────────────────────────────
function getWeekOptions() {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
        const d = new Date(now);
        d.setDate(d.getDate() - i * 7);
        const { start, end } = getWeekRange(d);
        const value = start.toISOString().split('T')[0];
        const label = `Week of ${start.toLocaleDateString('en-KE', { month: 'short', day: 'numeric' })}`;
        options.push({ value, label, start, end });
    }
    return options;
}

// ─── Term options generator ─────────────────────────────────────────────────
function getTermOptions() {
    const options = [];
    const now = new Date();
    const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
    for (const year of years) {
        for (const t of TERM_OPTIONS) {
            const { start, end } = getTermDates(t.term, year);
            options.push({
                value: `${year}-T${t.term}`,
                label: `${t.label} ${year}`,
                start,
                end,
            });
        }
    }
    return options;
}

const PaymentModal = ({ isOpen, onClose, lease, onPaymentComplete }) => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [paymentSummary, setPaymentSummary] = useState(null);
    const [formData, setFormData] = useState({
        amount: '',
        payment_type: 'rent',
        payment_method: 'cash',
        payment_period_start: '',
        payment_period_end: '',
        notes: ''
    });

    const billingCycle = lease?.billing_cycle || 'monthly';

    useEffect(() => {
        if (isOpen && lease && lease.id) {
            fetchPayments();
            fetchPaymentSummary();
        }
    }, [isOpen, lease]);

    const fetchPayments = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/leases/${lease.id}/payments`);
            setPayments(response.data.data.data || []);
        } catch (error) {
            console.error('Error fetching payments:', error);
            setPayments([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPaymentSummary = async () => {
        try {
            if (!lease || !lease.rent_amount) {
                console.warn('Lease data incomplete:', lease);
                return;
            }

            const startDate = new Date(lease.start_date);
            const endDate = new Date(lease.end_date);
            const months = (endDate.getFullYear() - startDate.getFullYear()) * 12 +
                          (endDate.getMonth() - startDate.getMonth());

            const expectedTotal = parseFloat(lease.rent_amount) * months;
            const paidAmount = payments.filter(p => p.status === 'completed')
                .reduce((sum, p) => sum + parseFloat(p.amount), 0);

            setPaymentSummary({
                expected_total: expectedTotal,
                paid_amount: paidAmount,
                balance: expectedTotal - paidAmount,
                next_due_date: calculateNextDueDate(startDate)
            });
        } catch (error) {
            console.error('Error calculating summary:', error);
        }
    };

    const calculateNextDueDate = (startDate) => {
        const today = new Date();
        const dueDate = new Date(today);
        const paymentDueDay = lease?.payment_due_day || 1;
        dueDate.setDate(paymentDueDay);

        if (dueDate < today) {
            dueDate.setMonth(dueDate.getMonth() + 1);
        }
        return dueDate;
    };

    /**
     * Handle period selection based on billing cycle.
     * Sets payment_period_start and payment_period_end correctly
     * for monthly, termly, weekly, daily, and milestone cycles.
     */
    const handlePeriodChange = (value, cycle) => {
        let start = null;
        let end = null;

        switch (cycle) {
            case 'termly': {
                let year, termNum;
                if (value.includes('-T')) {
                    // value = "2026-T2" format
                    const [y, t] = value.split('-T');
                    year = parseInt(y);
                    termNum = parseInt(t);
                } else {
                    // value = ISO date string like "2026-05-01"
                    const d = new Date(value);
                    year = d.getFullYear();
                    const m = d.getMonth() + 1;
                    const def = TERM_OPTIONS.find(t => m >= t.startMonth + 1 && m <= t.endMonth + 1);
                    termNum = def ? def.term : 2;
                }
                const dates = getTermDates(termNum, year);
                if (dates) {
                    start = dates.start;
                    end = dates.end;
                }
                break;
            }
            case 'weekly': {
                // value = ISO date string for Monday of the week
                const monday = new Date(value);
                const sunday = new Date(monday);
                sunday.setDate(monday.getDate() + 6);
                start = monday;
                end = sunday;
                break;
            }
            case 'daily': {
                const day = new Date(value);
                start = day;
                end = day;
                break;
            }
            case 'milestone': {
                // Milestone: use the payment date itself
                const today = new Date();
                start = today;
                end = today;
                break;
            }
            default: // monthly
            {
                const [yearStr, monthStr] = value.split('-');
                const y = parseInt(yearStr);
                const m = parseInt(monthStr);
                start = new Date(y, m - 1, 1);
                end = new Date(y, m, 0); // last day of month
                break;
            }
        }

        if (start && end) {
            // Use local date strings instead of ISO to avoid timezone shifts
            // (e.g. new Date(2026, 6, 1) = July 1 in Nairobi EAT, but .toISOString() shifts to June 30 UTC)
            const formatLocal = (d) => {
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            setFormData({
                ...formData,
                payment_period_start: formatLocal(start),
                payment_period_end: formatLocal(end),
            });
        }
    };

    const handleSubmitPayment = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const paymentData = {
                lease_id: lease.id,
                amount: parseFloat(formData.amount),
                payment_type: formData.payment_type,
                payment_method: formData.payment_method,
                payment_period_start: formData.payment_period_start,
                payment_period_end: formData.payment_period_end,
                metadata: { notes: formData.notes }
            };

            const response = await api.post('/payments', paymentData);
            console.log('Payment recorded:', response.data);

            setShowPaymentForm(false);
            setFormData({
                amount: '',
                payment_type: 'rent',
                payment_method: 'cash',
                payment_period_start: '',
                payment_period_end: '',
                notes: ''
            });
            fetchPayments();
            if (onPaymentComplete) onPaymentComplete();
            alert('Payment recorded successfully!');
        } catch (error) {
            console.error('Error recording payment:', error);
            alert('Failed to record payment: ' + (error.response?.data?.error || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Completed' },
            pending: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, text: 'Pending' },
            processing: { color: 'bg-blue-100 text-blue-800', icon: AlertCircle, text: 'Processing' },
            failed: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Failed' },
            refunded: { color: 'bg-gray-100 text-gray-800', icon: XCircle, text: 'Refunded' }
        };
        const badge = badges[status] || badges.pending;
        const Icon = badge.icon;
        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                <Icon className="h-3 w-3 mr-1" />
                {badge.text}
            </span>
        );
    };

    // Don't render if no lease
    if (!lease || !lease.id) {
        return null;
    }

    // ─── Render period selector based on billing cycle ──────────────────
    const renderPeriodSelector = () => {
        const cycle = billingCycle;

        switch (cycle) {
            case 'termly': {
                const opts = getTermOptions();
                // Use ISO start-date string for matching with formData.payment_period_start
                const selectedVal = formData.payment_period_start || '';
                return (
                    <select
                        value={selectedVal}
                        onChange={(e) => handlePeriodChange(e.target.value, cycle)}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                        <option value="">Select Term</option>
                        {opts.map((opt, i) => (
                            <option key={i} value={opt.start.toISOString().split('T')[0]}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                );
            }

            case 'weekly': {
                const opts = getWeekOptions();
                return (
                    <select
                        value={formData.payment_period_start || ''}
                        onChange={(e) => handlePeriodChange(e.target.value, cycle)}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                        <option value="">Select Week</option>
                        {opts.map((opt, i) => (
                            <option key={i} value={opt.start.toISOString().split('T')[0]}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                );
            }

            case 'daily': {
                return (
                    <input
                        type="date"
                        value={formData.payment_period_start || ''}
                        onChange={(e) => handlePeriodChange(e.target.value, cycle)}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                );
            }

            case 'milestone': {
                return (
                    <input
                        type="text"
                        value="Milestone (One-time)"
                        disabled
                        className="w-full rounded-md border-gray-300 bg-gray-100 shadow-sm text-gray-500"
                    />
                );
            }

            default: // monthly
            {
                return (
                    <select
                        value={`${formData.payment_period_start?.split('T')[0] || ''}`}
                        onChange={(e) => handlePeriodChange(e.target.value, cycle)}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                        <option value="">Select Month</option>
                        {getMonthOptions().map((opt, i) => {
                            const val = `${opt.year}-${String(opt.month).padStart(2, '0')}`;
                            const sel = formData.payment_period_start
                                ? `${formData.payment_period_start.split('-')[0]}-${formData.payment_period_start.split('-')[1]}`
                                : '';
                            return (
                                <option key={i} value={val} selected={sel === val}>
                                    {opt.label}
                                </option>
                            );
                        })}
                    </select>
                );
            }
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Payments - ${lease.asset_name || 'Lease'} (Unit ${lease.unit_number || ''})`} size="lg">
            <div className="space-y-6">
                {/* Payment Summary */}
                {paymentSummary && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-3">Payment Summary</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <p className="text-sm text-gray-500">Expected Total</p>
                                <p className="text-lg font-semibold text-gray-900">KES {paymentSummary.expected_total.toLocaleString()}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Paid Amount</p>
                                <p className="text-lg font-semibold text-green-600">KES {paymentSummary.paid_amount.toLocaleString()}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Balance</p>
                                <p className="text-lg font-semibold text-orange-600">KES {paymentSummary.balance.toLocaleString()}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Next Payment Due</p>
                                <p className="text-lg font-semibold text-indigo-600">{paymentSummary.next_due_date?.toLocaleDateString()}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Billing Cycle Indicator */}
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Calendar className="h-4 w-4" />
                    <span>Billing cycle: <strong className="text-gray-700 capitalize">{billingCycle}</strong></span>
                </div>

                {/* Add Payment Button */}
                <div className="flex justify-between items-center">
                    <h3 className="font-semibold text-gray-900">Payment History</h3>
                    <button
                        onClick={() => setShowPaymentForm(!showPaymentForm)}
                        className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
                    >
                        {showPaymentForm ? 'Cancel' : '+ Record Payment'}
                    </button>
                </div>

                {/* Payment Form */}
                {showPaymentForm && (
                    <form onSubmit={handleSubmitPayment} className="bg-gray-50 p-4 rounded-lg space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Payment Type
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                {PAYMENT_TYPES.map((type) => {
                                    const Icon = type.icon;
                                    return (
                                        <button
                                            key={type.value}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, payment_type: type.value })}
                                            className={`p-2 border rounded-lg text-center transition-all ${
                                                formData.payment_type === type.value
                                                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700 ring-2 ring-indigo-200'
                                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            <Icon className="h-4 w-4 mx-auto mb-1" />
                                            <span className="text-xs font-medium block">{type.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (KES)</label>
                                <input
                                    type="number"
                                    required
                                    value={formData.amount}
                                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    placeholder="25000"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                                <select
                                    value={formData.payment_method}
                                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                >
                                    <option value="cash">Cash</option>
                                    <option value="bank_transfer">Bank Transfer</option>
                                    <option value="mpesa_stk">M-Pesa STK Push</option>
                                    <option value="mpesa_paybill">M-Pesa Paybill</option>
                                    <option value="cheque">Cheque</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Payment Period
                                    {billingCycle !== 'monthly' && (
                                        <span className="ml-2 text-xs text-indigo-600 font-normal">
                                            ({billingCycle === 'termly' ? 'per term' : billingCycle === 'weekly' ? 'per week' : billingCycle === 'daily' ? 'per day' : 'one-time'})
                                        </span>
                                    )}
                                </label>
                                {renderPeriodSelector()}
                                {formData.payment_period_start && formData.payment_period_end && (
                                    <p className="mt-1 text-xs text-gray-500">
                                        {new Date(formData.payment_period_start).toLocaleDateString()} — {new Date(formData.payment_period_end).toLocaleDateString()}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    rows={2}
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    placeholder="Additional notes..."
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button type="button" onClick={() => setShowPaymentForm(false)} className="px-3 py-1 border rounded-md">Cancel</button>
                            <button type="submit" disabled={submitting} className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700">
                                {submitting ? 'Processing...' : 'Record Payment'}
                            </button>
                        </div>
                    </form>
                )}

                {/* Payments Table */}
                {loading ? (
                    <div className="text-center py-8">Loading payments...</div>
                ) : payments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No payments recorded yet.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {payments.map((payment) => (
                                    <tr key={payment.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                                            {new Date(payment.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                                            KES {parseFloat(payment.amount).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm capitalize">
                                            {payment.payment_type}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm capitalize">
                                            {payment.payment_method?.replace('_', ' ')}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                                            {payment.payment_period_start ? (
                                                `${new Date(payment.payment_period_start).toLocaleDateString()} - ${new Date(payment.payment_period_end).toLocaleDateString()}`
                                            ) : '-'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {getStatusBadge(payment.status)}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                                            {payment.mpesa_receipt_number || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default PaymentModal;
